import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { adminApi, FitReference } from '../services/adminApi';
import { API_BASE_URL } from '../config/environment';
import { getMeasurementsForCategories, getProductTypesForCategories } from '../utils/categoryMeasurements';
import CategoryMultiSelect from '../components/CategoryMultiSelect';
import './AdminProducts.css';
import { handleImageError, PLACEHOLDER_IMAGE } from '../utils/imageUtils';

// Helper function to get proper image URL
const getImageUrl = (imagePath: string | undefined): string => {
  if (!imagePath) {
    return PLACEHOLDER_IMAGE;
  }
  
  // Bucket URLs (Tigris/Railway, Cloudflare R2) → route through our proxy; r2.dev is
  // rate-limited and blocked on some networks
  if (imagePath.includes('storageapi.dev') || imagePath.includes('railway.storage') || imagePath.includes('.r2.dev') || imagePath.includes('r2.cloudflarestorage.com')) {
    const key = imagePath.replace(/^https?:\/\/[^/]+\//, '');
    return `/api/upload/proxy/${key}`;
  }

  // Our proxy URL (new uploads) or other absolute URLs
  if (imagePath.startsWith('https://') || imagePath.startsWith('http://') || imagePath.startsWith('/api/upload/proxy/')) {
    return imagePath;
  }
  
  // If it's a base64 image, use fallback
  if (imagePath.startsWith('data:image/')) {
    return PLACEHOLDER_IMAGE;
  }
  
  // If it's a file path, use as is (for static assets)
  if (imagePath.startsWith('/products/')) {
    return imagePath;
  }
  
  // Fallback
  return PLACEHOLDER_IMAGE;
};

interface Product {
  id: number;
  name?: string | { id: number; name: string; description?: string; image?: string; createdAt?: string; updatedAt?: string; };
  title?: string | { id: number; name: string; description?: string; image?: string; createdAt?: string; updatedAt?: string; };
  description?: string | { id: number; name: string; description?: string; image?: string; createdAt?: string; updatedAt?: string; };
  price: number;
  originalPrice?: number | null;
  images: string[];
  hoverImage?: string;
  condition?: string;
  fitReferenceId?: number | null;
  isSold?: boolean;
  category: string | { id: number; name: string; description?: string; image?: string; createdAt?: string; updatedAt?: string; };
  categoryIds?: number[];
  categories?: { id: number; name: string }[];
  size?: string;
  inStock: boolean;
  stockQuantity?: number;
  measurements?: { [key: string]: string }; // Product measurements
  _locallyModified?: boolean; // Flag to indicate local-only changes
}

const AdminProducts: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [fitReferences, setFitReferences] = useState<FitReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'published' | 'draft' | 'archived'>('published');

  useEffect(() => {
    loadProducts();
    loadCategories();
    adminApi.getFitReferences()
      .then(setFitReferences)
      .catch(err => console.error('Failed to load fit references:', err));
  }, []);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/categories`);
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getProducts();
      // Handle different API response formats
      let allProductsList: Product[] = [];
      if (Array.isArray(data)) {
        allProductsList = data;
      } else if (data && Array.isArray(data.products)) {
        allProductsList = data.products;
      } else if (data && Array.isArray(data.data)) {
        allProductsList = data.data;
      } else {
        allProductsList = [];
      }
      
      setAllProducts(allProductsList);
      filterProductsByStatus(allProductsList, activeTab);
    } catch (err: any) {
      console.error('Failed to load products:', err);
      setError(err.message || 'Failed to load products');
      setProducts([]);
      setAllProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const filterProductsByStatus = (productsList: Product[], status: string) => {
    const filtered = productsList.filter(product => {
      const productStatus = (product as any).status || 'published';
      return productStatus === status;
    });
    setProducts(filtered);
  };

  const handleTabChange = (tab: 'published' | 'draft' | 'archived') => {
    setActiveTab(tab);
    filterProductsByStatus(allProducts, tab);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setShowEditModal(true);
  };

  const handleSaveProduct = async (product: Product) => {
    try {
      // Clean and validate the data before sending
      const updateData = {
        name: typeof product.name === 'string' ? product.name : product.name?.name || '',
        description: typeof product.description === 'string' ? product.description : product.description?.name || '',
        price: parseFloat(String(product.price)) || 0,
        originalPrice: product.originalPrice ? parseFloat(String(product.originalPrice)) : undefined,
        images: Array.isArray(product.images) ? product.images : [PLACEHOLDER_IMAGE],
        hoverImage: product.hoverImage || '',
        size: String(product.size || ''),
        inStock: Boolean(product.inStock),
        stockQuantity: product.stockQuantity !== undefined ? parseInt(String(product.stockQuantity)) : 1,
        categoryIds: product.categoryIds || [],
        condition: product.condition || '',
        fitReferenceId: product.fitReferenceId ?? null,
        isSold: Boolean(product.isSold),
        measurements: product.measurements || {},
        productType: (product as any).productType || undefined
      };
      
      // Additional validation - remove any undefined values (null clears the fit reference)
      Object.keys(updateData).forEach(key => {
        const typedKey = key as keyof typeof updateData;
        if (updateData[typedKey] === undefined) {
          console.warn(`Removing undefined field: ${key}`);
          delete (updateData as any)[key];
        }
      });
      
      // Try to update the product
      const result = await adminApi.updateProduct(product.id, updateData);
      
      // Update was successful - reload products and close modal
      setSuccessMessage('Product updated successfully in database!');
      setShowEditModal(false);
      setEditingProduct(null);
      setError('');
      
      // Reload products from server to show the changes
      try {
        await loadProducts();
      } catch (loadError) {
        // If reload fails, update local state as fallback
        setProducts(prevProducts => 
          prevProducts.map(p => 
            p.id === product.id 
              ? { ...p, ...updateData }
              : p
          )
        );
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (err: any) {
      console.error('Error updating product:', err);
      console.error('Full error object:', JSON.stringify(err, Object.getOwnPropertyNames(err)));
      
      if (err.message?.includes('Unable to connect')) {
        setError('Cannot connect to server. Please check if the backend is running.');
      } else if (err.response?.data) {
        setError(`Server error: ${err.response.data.message || err.response.data.error || 'Unknown error'}`);
      } else {
        setError(`Update failed: ${err.message || 'Unknown error occurred'}`);
      }
    }
  };

  const handleDeleteProduct = async (productId: number) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await adminApi.deleteProduct(productId);
        await loadProducts();
      } catch (err: any) {
        setError(err.message || 'Failed to delete product');
      }
    }
  };

  const handleStatusChange = async (productId: number, newStatus: string) => {
    try {
      console.log(`🔄 Admin: Changing product ${productId} status to: ${newStatus}`);
      await adminApi.updateProductStatus(productId, newStatus);
      
      // Update both allProducts and filtered products
      setAllProducts(prevAll => 
        prevAll.map(p => 
          p.id === productId 
            ? { ...p, status: newStatus } as any
            : p
        )
      );
      
      // Reload to get fresh data and apply current filter
      await loadProducts();
    } catch (err: any) {
      console.error('Status update failed:', err);
      setError(err.message || 'Failed to update product status');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Loading Products...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-products">
        <div className="products-header">
          <div className="header-content">
            <h1>Products Management</h1>
            <p>Manage your product catalog</p>
          </div>
          <div className="header-actions">
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/products/add')}
            >
              <span className="btn-icon">➕</span>
              Add New Product
            </button>
            <button className="btn btn-secondary" onClick={loadProducts}>
              <span className="btn-icon">🔄</span>
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9V13M12 17.0195V17M10.29 3.86L1.82 18C1.64468 18.3024 1.55274 18.6453 1.55376 18.9945C1.55478 19.3437 1.64868 19.6861 1.82601 19.9877C2.00333 20.2893 2.25952 20.5368 2.56983 20.7039C2.88014 20.871 3.22929 20.9519 3.58 20.94H20.42C20.7707 20.9519 21.1199 20.871 21.4302 20.7039C21.7405 20.5368 21.9967 20.2893 22.174 19.9877C22.3513 19.6861 22.4452 19.3437 22.4462 18.9945C22.4473 18.6453 22.3553 18.3024 22.18 18L13.71 3.86C13.5318 3.56611 13.2807 3.32312 12.9812 3.15448C12.6817 2.98585 12.3438 2.89725 12 2.89725C11.6562 2.89725 11.3183 2.98585 11.0188 3.15448C10.7193 3.32312 10.4682 3.56611 10.29 3.86V3.86Z" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            {error}
            <button onClick={() => setError('')} className="close-error">×</button>
          </div>
        )}

        {successMessage && (
          <div className="success-message">
            <span className="success-icon">💫</span>
            {successMessage}
            <button onClick={() => setSuccessMessage('')} className="close-success">×</button>
          </div>
        )}

        {/* Status Tabs */}
        <div className="status-tabs">
          <div className="tab-buttons">
            <button 
              className={`tab-button ${activeTab === 'published' ? 'active' : ''}`}
              onClick={() => handleTabChange('published')}
            >
              <span className="tab-icon">✨</span>
              Published
              <span className="tab-count">
                {allProducts.filter(p => ((p as any).status || 'published') === 'published').length}
              </span>
            </button>
            <button 
              className={`tab-button ${activeTab === 'draft' ? 'active' : ''}`}
              onClick={() => handleTabChange('draft')}
            >
              <span className="tab-icon">🌙</span>
              Draft
              <span className="tab-count">
                {allProducts.filter(p => ((p as any).status || 'published') === 'draft').length}
              </span>
            </button>
            <button 
              className={`tab-button ${activeTab === 'archived' ? 'active' : ''}`}
              onClick={() => handleTabChange('archived')}
            >
              <span className="tab-icon">🗃️</span>
              Archived
              <span className="tab-count">
                {allProducts.filter(p => ((p as any).status || 'published') === 'archived').length}
              </span>
            </button>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="products-table-container desktop-table">
          <table className="products-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Category</th>
                <th>Price</th>
                <th>Size</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(products) && products.map((product) => (
                <tr key={product.id} className={product._locallyModified ? 'locally-modified' : ''}>
                  <td>
                    <div className="product-image">
                      {product.images?.[0] ? (
                        <img 
                          src={getImageUrl(product.images[0])} 
                          alt={typeof product.name === 'string' ? product.name : 
                               typeof product.name === 'object' && product.name?.name ? product.name.name : 
                               'Product image'}
                          onError={handleImageError}
                        />
                      ) : (
                        <div className="image-placeholder">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 4H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="13" r="4" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="admin-product-info">
                      <h4>
                        {typeof product.name === 'string' ? product.name :
                         typeof product.name === 'object' && product.name?.name ? product.name.name :
                         typeof product.title === 'string' ? product.title :
                         typeof product.title === 'object' && product.title?.name ? product.title.name :
                         'Untitled Product'}
                        {product._locallyModified && (
                          <span className="local-change-indicator" title="Changes not saved to database">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 9V13M12 17.0195V17M10.29 3.86L1.82 18C1.64468 18.3024 1.55274 18.6453 1.55376 18.9945C1.55478 19.3437 1.64868 19.6861 1.82601 19.9877C2.00333 20.2893 2.25952 20.5368 2.56983 20.7039C2.88014 20.871 3.22929 20.9519 3.58 20.94H20.42C20.7707 20.9519 21.1199 20.871 21.4302 20.7039C21.7405 20.5368 21.9967 20.2893 22.174 19.9877C22.3513 19.6861 22.4452 19.3437 22.4462 18.9945C22.4473 18.6453 22.3553 18.3024 22.18 18L13.71 3.86C13.5318 3.56611 13.2807 3.32312 12.9812 3.15448C12.6817 2.98585 12.3438 2.89725 12 2.89725C11.6562 2.89725 11.3183 2.98585 11.0188 3.15448C10.7193 3.32312 10.4682 3.56611 10.29 3.86V3.86Z" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </span>
                        )}
                      </h4>
                      <p>
                        {typeof product.description === 'string' && product.description ? 
                          product.description.substring(0, 100) + '...' :
                         typeof product.description === 'object' && product.description?.name ?
                          product.description.name.substring(0, 100) + '...' :
                         'No description available'}
                      </p>
                    </div>
                  </td>
                  <td>
                    <span className="category-badge">
                      {product.categories?.length
                        ? product.categories.map(c => c.name).join(', ')
                        : typeof product.category === 'string' 
                        ? product.category 
                        : product.category?.name || 'Unknown'}
                    </span>
                  </td>
                  <td>
                    <div className="product-price">€{product.price ? Number(product.price).toFixed(2) : '0.00'}</div>
                  </td>
                  <td>
                    <div className="product-size">
                      <span className="size-badge">{product.size || 'N/A'}</span>
                    </div>
                  </td>
                  <td>
                    <div className="stock-info">
                      <span className={`stock-status ${product.inStock ? 'in-stock' : 'out-of-stock'}`}>
                        {product.isSold ? 'Sold' : product.inStock ? 'In Stock' : 'Out of Stock'}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="status-controls">
                      <select 
                        className={`status-select ${(product as any).status || 'published'}`}
                        value={(product as any).status || 'published'}
                        onChange={(e) => handleStatusChange(product.id, e.target.value)}
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-action btn-edit"
                        onClick={() => handleEditProduct(product)}
                        title="Edit Product"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#197cc7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M18.5 2.50023C18.8978 2.10243 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.10243 21.5 2.50023C21.8978 2.89804 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.10243 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="#197cc7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <button 
                        className="btn-action btn-delete"
                        onClick={() => handleDeleteProduct(product.id)}
                        title="Delete Product"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6H5H21" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6H19Z" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="products-cards-container mobile-cards">
          {Array.isArray(products) && products.map((product) => (
            <div key={product.id} className={`product-card ${product._locallyModified ? 'locally-modified' : ''}`}>
              <div className="card-image">
                {product.images?.[0] ? (
                  <img 
                    src={getImageUrl(product.images[0])} 
                    alt={typeof product.name === 'string' ? product.name : 
                         typeof product.name === 'object' && product.name?.name ? product.name.name : 
                         'Product image'}
                    onError={handleImageError}
                  />
                ) : (
                  <div className="image-placeholder">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 4H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="13" r="4" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>
              
              <div className="card-content">
                <div className="card-info">
                  <h4 className="card-title">
                    {typeof product.name === 'string' ? product.name :
                     typeof product.name === 'object' && product.name?.name ? product.name.name :
                     typeof product.title === 'string' ? product.title :
                     typeof product.title === 'object' && product.title?.name ? product.title.name :
                     'Untitled Product'}
                    {product._locallyModified && (
                      <span className="local-change-indicator" title="Changes not saved to database">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 9V13M12 17.0195V17M10.29 3.86L1.82 18C1.64468 18.3024 1.55274 18.6453 1.55376 18.9945C1.55478 19.3437 1.64868 19.6861 1.82601 19.9877C2.00333 20.2893 2.25952 20.5368 2.56983 20.7039C2.88014 20.871 3.22929 20.9519 3.58 20.94H20.42C20.7707 20.9519 21.1199 20.871 21.4302 20.7039C21.7405 20.5368 21.9967 20.2893 22.174 19.9877C22.3513 19.6861 22.4452 19.3437 22.4462 18.9945C22.4473 18.6453 22.3553 18.3024 22.18 18L13.71 3.86C13.5318 3.56611 13.2807 3.32312 12.9812 3.15448C12.6817 2.98585 12.3438 2.89725 12 2.89725C11.6562 2.89725 11.3183 2.98585 11.0188 3.15448C10.7193 3.32312 10.4682 3.56611 10.29 3.86V3.86Z" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    )}
                  </h4>
                  
                  <div className="card-detail">
                    <span className="detail-label">Category:</span>
                    <span className="category-badge">
                      {product.categories?.length
                        ? product.categories.map(c => c.name).join(', ')
                        : typeof product.category === 'string' 
                        ? product.category 
                        : product.category?.name || 'Unknown'}
                    </span>
                  </div>
                  
                  <div className="card-detail">
                    <span className="detail-label">Price:</span>
                    <span className="product-price">€{product.price ? Number(product.price).toFixed(2) : '0.00'}</span>
                  </div>
                  
                  <div className="card-detail">
                    <span className="detail-label">Size:</span>
                    <span className="size-badge">{product.size || 'N/A'}</span>
                  </div>
                  
                  <div className="card-detail">
                    <span className="detail-label">Stock:</span>
                    <span className={`stock-status ${product.inStock ? 'in-stock' : 'out-of-stock'}`}>
                      {product.isSold ? 'Sold' : product.inStock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </div>
                  
                  <div className="card-detail">
                    <span className="detail-label">Status:</span>
                    <span className={`status-badge ${product.inStock ? 'active' : 'inactive'}`}>
                      {product.inStock ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                
                <div className="card-actions">
                  <button 
                    className="btn-action btn-edit"
                    onClick={() => handleEditProduct(product)}
                    title="Edit Product"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '6px'}}>
                      <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#197cc7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18.5 2.50023C18.8978 2.10243 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.10243 21.5 2.50023C21.8978 2.89804 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.10243 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="#197cc7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Edit
                  </button>
                  <button 
                    className="btn-action btn-delete"
                    onClick={() => handleDeleteProduct(product.id)}
                    title="Delete Product"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '6px'}}>
                      <path d="M3 6H5H21" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6H19Z" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {(!Array.isArray(products) || products.length === 0) && (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 4V2C7 1.44772 7.44772 1 8 1H16C16.5523 1 17 1.44772 17 2V4H20C20.5523 4 21 4.44772 21 5C21 5.55228 20.5523 6 20 6H19V19C19 20.1046 18.1046 21 17 21H7C5.89543 21 5 20.1046 5 19V6H4C3.44772 6 3 5.55228 3 5C3 4.44772 3.44772 4 4 4H7ZM9 3V4H15V3H9ZM7 6V19H17V6H7Z" fill="#900e9b"/>
              </svg>
            </div>
            <h3>No Products Found</h3>
            <p>Start by adding your first product to the catalog</p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/products/add')}
            >
              Add First Product
            </button>
          </div>
        )}

        {/* Edit Product Modal */}
        {showEditModal && editingProduct && (
          <ProductEditModal
            product={editingProduct}
            categories={categories}
            fitReferences={fitReferences}
            onSave={handleSaveProduct}
            onCancel={() => {
              setShowEditModal(false);
              setEditingProduct(null);
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

interface ProductEditModalProps {
  product: Product;
  categories: any[];
  fitReferences: FitReference[];
  onSave: (product: Product) => Promise<void>;
  onCancel: () => void;
}

const ProductEditModal: React.FC<ProductEditModalProps> = ({ product, categories, fitReferences, onSave, onCancel }) => {
  const [formData, setFormData] = useState<Product>(product);
  const [measurements, setMeasurements] = useState<{ [key: string]: string }>(product.measurements || {});
  const [productType, setProductType] = useState<string>((product as any).productType || '');
  const [categoryIds, setCategoryIds] = useState<number[]>(
    product.categoryIds?.length
      ? product.categoryIds
      : typeof product.category === 'object' && product.category?.id ? [product.category.id] : []
  );
  // Images already uploaded stay as URLs; new picks are uploaded on save
  const [images, setImages] = useState<string[]>(Array.isArray(product.images) ? product.images : []);
  const [newImageFiles, setNewImageFiles] = useState<File[]>([]);
  const [newImagePreviews, setNewImagePreviews] = useState<string[]>([]);
  const [hoverImage, setHoverImage] = useState<string>(product.hoverImage || '');
  const [hoverImageFile, setHoverImageFile] = useState<File | null>(null);
  const [hoverImagePreview, setHoverImagePreview] = useState<string>('');
  const [sold, setSold] = useState<boolean>(Boolean(product.isSold));
  const [saving, setSaving] = useState(false);
  const selectedCategoryNames = categories
    .filter((cat: any) => categoryIds.includes(cat.id))
    .map((cat: any) => cat.name as string);

  useEffect(() => {
    // Load measurements when modal opens
    const loadMeasurements = async () => {
      try {
        const productMeasurements = await adminApi.getProductMeasurements(product.id);
        setMeasurements(productMeasurements);
      } catch (error) {
        console.error('Failed to load measurements:', error);
      }
    };
    
    loadMeasurements();
  }, [product.id]);
  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const uploadedImages: string[] = [];
      for (const file of newImageFiles) {
        const result = await adminApi.uploadImage(file);
        uploadedImages.push(result.imageUrl);
      }
      const finalHoverImage = hoverImageFile ? (await adminApi.uploadImage(hoverImageFile)).imageUrl : hoverImage;

      const stockQuantity = Math.max(0, Number(formData.stockQuantity) || 0);

      await onSave({
        ...formData,
        images: [...images, ...uploadedImages],
        hoverImage: finalHoverImage,
        stockQuantity,
        inStock: stockQuantity > 0 && !sold,
        isSold: sold,
        categoryIds,
        measurements,
        productType
      } as any);
    } catch (error: any) {
      alert(error.message || 'Failed to upload images');
    } finally {
      setSaving(false);
    }
  };

  const handleMeasurementChange = (measurementName: string, value: string) => {
    setMeasurements(prev => ({
      ...prev,
      [measurementName]: value
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? 0 : parseFloat(value) || 0) : 
               type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleNewImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setNewImageFiles(prev => [...prev, ...files]);
    setNewImagePreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
    e.target.value = '';
  };

  const removeNewImage = (index: number) => {
    URL.revokeObjectURL(newImagePreviews[index]);
    setNewImageFiles(prev => prev.filter((_, i) => i !== index));
    setNewImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleHoverImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (hoverImagePreview) URL.revokeObjectURL(hoverImagePreview);
    setHoverImageFile(file);
    setHoverImagePreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const removeHoverImage = () => {
    if (hoverImagePreview) URL.revokeObjectURL(hoverImagePreview);
    setHoverImageFile(null);
    setHoverImagePreview('');
    setHoverImage('');
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Product</h2>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="edit-product-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">Product Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={typeof formData.name === 'string' ? formData.name : formData.name?.name || ''}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="categoryIds">Categories</label>
              <CategoryMultiSelect
                id="categoryIds"
                categories={categories}
                selectedIds={categoryIds}
                onChange={setCategoryIds}
              />
            </div>

            <div className="form-group">
              <label htmlFor="price">Price (€)</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price || 0}
                onChange={handleChange}
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="originalPrice">Original Price (€)</label>
              <input
                type="number"
                id="originalPrice"
                name="originalPrice"
                value={formData.originalPrice || ''}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="size">Size</label>
              <input
                type="text"
                id="size"
                name="size"
                value={formData.size || ''}
                onChange={handleChange}
                placeholder="e.g., S, M, L, XL, 38, 40"
              />
            </div>

            <div className="form-group">
              <label htmlFor="stockQuantity">Stock Quantity</label>
              <input
                type="number"
                id="stockQuantity"
                name="stockQuantity"
                value={formData.stockQuantity || 0}
                onChange={handleChange}
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="condition">Condition</label>
              <input
                type="text"
                id="condition"
                name="condition"
                value={formData.condition || ''}
                onChange={handleChange}
                placeholder="e.g., Excellent, small mark on the sleeve"
              />
            </div>

            <div className="form-group">
              <label htmlFor="fitReferenceId">Fit Reference</label>
              <select
                id="fitReferenceId"
                value={formData.fitReferenceId ?? ''}
                onChange={(e) => setFormData(prev => ({ ...prev, fitReferenceId: e.target.value ? Number(e.target.value) : null }))}
              >
                <option value="">No fit reference</option>
                {fitReferences.map(ref => (
                  <option key={ref.id} value={ref.id}>
                    {ref.name} ({ref.type === 'model' ? 'Model' : 'Dressform'})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group checkbox-group full-width">
              <input
                type="checkbox"
                id="sold"
                checked={sold}
                onChange={(e) => setSold(e.target.checked)}
              />
              <label htmlFor="sold">Sold (stays in the shop with a SOLD badge, can't be added to cart)</label>
            </div>

          </div>

          {/* Product Type Selection for Belts & Scarves */}
          {(() => {
            const productTypes = getProductTypesForCategories(selectedCategoryNames);
            
            if (productTypes.length > 0) {
              return (
                <div className="form-group full-width">
                  <label htmlFor="productType">Product Type</label>
                  <select
                    id="productType"
                    name="productType"
                    value={productType}
                    onChange={(e) => setProductType(e.target.value)}
                  >
                    <option value="">Select Product Type</option>
                    {productTypes.map(type => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }
            return null;
          })()}

          {/* Measurements Section */}
          {(() => {
            const categoryName = selectedCategoryNames.join(', ');
            const measurementFields = getMeasurementsForCategories(selectedCategoryNames, productType);
            
            if (measurementFields.length > 0) {
              return (
                <div className="measurements-section">
                  <h3>📐 Measurements for {categoryName}{productType && ` - ${productType}`}</h3>
                  <div className="measurements-grid">
                    {measurementFields.map((field) => (
                      <div key={field.name} className="form-group">
                        <label htmlFor={`measurement-${field.name}`}>
                          {field.label}
                        </label>
                        <input
                          type="text"
                          id={`measurement-${field.name}`}
                          name={`measurement-${field.name}`}
                          value={measurements[field.name] || ''}
                          onChange={(e) => handleMeasurementChange(field.name, e.target.value)}
                          placeholder={field.placeholder}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          })()}

          <div className="form-group full-width">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={typeof formData.description === 'string' ? formData.description : formData.description?.name || ''}
              onChange={handleChange}
              rows={4}
            />
          </div>

          {/* Product Images */}
          <div className="edit-images-section">
            <h3>Product Images</h3>
            <div className="edit-images-grid">
              {images.map((image, index) => (
                <div key={image + index} className="edit-image">
                  <img src={getImageUrl(image)} alt={`Product ${index + 1}`} onError={handleImageError} />
                  <button type="button" className="edit-image-remove" onClick={() => setImages(prev => prev.filter((_, i) => i !== index))}>×</button>
                </div>
              ))}
              {newImagePreviews.map((preview, index) => (
                <div key={preview} className="edit-image new">
                  <img src={preview} alt={`New ${index + 1}`} />
                  <button type="button" className="edit-image-remove" onClick={() => removeNewImage(index)}>×</button>
                </div>
              ))}
              <label className="edit-image-add">
                <input type="file" accept="image/*" multiple onChange={handleNewImages} hidden />
                + Add
              </label>
            </div>
          </div>

          {/* Hover Image */}
          <div className="edit-images-section">
            <h3>Hover Image (Optional)</h3>
            <div className="edit-images-grid">
              {(hoverImagePreview || hoverImage) && (
                <div className="edit-image">
                  <img src={hoverImagePreview || getImageUrl(hoverImage)} alt="Hover" onError={handleImageError} />
                  <button type="button" className="edit-image-remove" onClick={removeHoverImage}>×</button>
                </div>
              )}
              <label className="edit-image-add">
                <input type="file" accept="image/*" onChange={handleHoverImage} hidden />
                {hoverImagePreview || hoverImage ? 'Replace' : '+ Add'}
              </label>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProducts;